import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import CONSTANT from "../../../../constants/Constant";
import { useDispatch, useSelector } from "react-redux";
import routes from "../../../../constants/Routes";
import { setBiometricModalOpen, clearChatbotNewState } from "../../../../features/chatbotNew/chatbotNewSlice";
import MovementModal from "../../MovementModal/MovementModal";
import useNewChatbotBody from "../hooks/useNewChatbotBody";
import IntakeFormWrapper from "../../intake-form/IntakeFormWrapper/IntakeFormWrapper";
import ExamService from "../../../../services/ExamService";
import { saveIntake } from "../../intake-form/intake-form-service";
import AppContext from "../../../../contexts/AppContext";
import useIntakeFrom from "../../intake-form/useIntakeFrom";
import GLOBAL from "../../../../global";
import { _getWaitingRoomByInvitationInfo } from "../../../../utils/Utils";
import { Modal, ModalHeader } from "../../../modal/Modal";
import crossBtn from "../../../../assets/img/utils/cross.svg";
import { useTenantFeatures } from "../../../../features/tenantFeatures/useTenantFeature";

const BiometricPrompt = () => {
    const chatBotNewState = useSelector((state) => state.chatBotNew);
    const { testId, invitationTypeName, bodyRegionList, biometricModalOpen, invitationId, answeredQuestions, biometricGroupHeaderDescription, groups } = chatBotNewState;
    const dispatch = useDispatch();
    const { handleCloseChatbotNew } = useNewChatbotBody();
    const { setTelemedicineProperties, resetTelemedicineKeys, storeUserInfoToLocalStorage } = useContext(AppContext);
    const { handleOpenIntakeForm, handleCloseIntakeForm } = useIntakeFrom();
    const history = useHistory();

  const [waitingRoomId, setWaitingRoomId] = useState("");
  const [telemedicineInvitationId, setTelemedicineInvitationId] = useState("");

  const isIntakeSigned = GLOBAL?.USER_INFO?.IsIntakeSigned || false;
  const { isExamineticsEnabled, isIntakeTelemedicineFormEnable } = useTenantFeatures();

  // Update GLOBAL.USER_INFO.Height from chatbot answers
  // Also update localStorage to persist the height value
  useEffect(() => {
    const heightQuestion = answeredQuestions?.find(
      (q) => q.questionId === CONSTANT.CHATBOT_ACCOUNT.HEIGHT,
    );
    if (heightQuestion?.answerValue) {
      const chatbotHeight = parseInt(heightQuestion.answerValue);

      // Only update if chatbot height is different from current value
      if (GLOBAL.USER_INFO.Height !== chatbotHeight) {
        GLOBAL.USER_INFO.Height = chatbotHeight;
        console.log(
          "Updated GLOBAL.USER_INFO.Height from chatbot:",
          GLOBAL.USER_INFO.Height,
        );

        // Persist to localStorage
        if (storeUserInfoToLocalStorage) {
          storeUserInfoToLocalStorage();
          console.log("Persisted updated Height to localStorage");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredQuestions]);

  // Initialize telemedicine (get waiting room info, check if intake needed)
  useEffect(() => {
    const initTelemedicine = async () => {
      const invitationType = (invitationTypeName || "").toLowerCase();
      if (invitationType === CONSTANT.REDIRECT_TYPE.TELEMEDICINE) {
        try {
          const { invitationId: teleInvId, waitingRoomId: waitRoomId } =
            await _getWaitingRoomByInvitationInfo(testId, invitationId);
          setTelemedicineInvitationId(teleInvId);
          setWaitingRoomId(waitRoomId);
        } catch (error) {
          console.error("Error getting telemedicine waiting room:", error);
        }
      }
    };
    initTelemedicine();
  }, [invitationTypeName, testId, invitationId]);

  const getMessage = () => {
    const invitationType = (invitationTypeName || "").toLowerCase();

    if (invitationType === CONSTANT.REDIRECT_TYPE.INCLINIC) {
      // EXACT MESSAGE for In-clinic
      return "Thank you for completing the questionnaire. Please be available at your scheduled clinic time.";
    } else if (invitationType === CONSTANT.REDIRECT_TYPE.TELEMEDICINE) {
      // EXACT MESSAGE for Telemedicine
      return "Please join the appointment with your provider at the scheduled time to complete your assessment.";
    } 

    // For Examinetics Self Assessment, show a neutral redirecting message if needed, 
    // but the goal is to skip the text entirely or show a loader.
    const isSelfAssessment = invitationType !== CONSTANT.REDIRECT_TYPE.INCLINIC && invitationType !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;
    if (isExamineticsEnabled && isSelfAssessment) {
      return "Redirecting to functional movements...";
    }

    // Use dynamic message from GroupId 3 if completed (the main completion message trigger)
    const targetGroup = (groups || []).find(
      (g) => g.groupId === 3 && g.completedGroupHeaderTitle
    );

    if (targetGroup) {
      return targetGroup.completedGroupHeaderTitle;
    }

    // Default/SELF initial message
    return biometricGroupHeaderDescription || "";
  };

  const handleOK = () => {
    // In-clinic: Redirect to landing page
    dispatch(clearChatbotNewState());
    handleCloseChatbotNew();
    window.location.href = `/#${routes.landing}`;
  };

  const onOpenTelemedicineModal = () => {
    // Telemedicine session opened successfully
  };

  const onCloseTelemedicineModal = () => {
    // When telemedicine session ends
    resetTelemedicineKeys();
    dispatch(clearChatbotNewState());
    handleCloseChatbotNew();
    window.location.href = isExamineticsEnabled ? `/#${routes.dashboard}` : `/#${routes.myAssessments}/false`;
  };

  const handleJoinTelemedicineNow = async (values) => {
    if (!isIntakeSigned && isIntakeTelemedicineFormEnable) {
      if (!values || values?.nativeEvent) {
        // Trigger global intake form modal if required
        handleOpenIntakeForm({
          bodyRegionList: bodyRegionList || [],
          invitationTypeName: invitationTypeName || "",
          onClickJoinTelemedicineNow: handleJoinTelemedicineNow,
          onClickJoinTelemedicineLater: handleJoinTelemedicineLater,
        });
        return;
      } else {
        await saveIntake(values);
        handleCloseIntakeForm();
      }
    }

    // This opens the telemedicine video call
    setTelemedicineProperties(
      telemedicineInvitationId,
      bodyRegionList,
      onOpenTelemedicineModal,
      onCloseTelemedicineModal,
    );
  };

  const handleJoinTelemedicineLater = async (values) => {
    handleCloseIntakeForm();
    if (!isIntakeSigned && isIntakeTelemedicineFormEnable && values && !values?.nativeEvent) {
      await saveIntake(values);
    }

    // Send email with waiting room info
    try {
      await ExamService.sendTelemedEmailForEmma({
        WaitingRoomId: waitingRoomId,
      });
    } catch (error) {
      console.error("Error sending telemedicine email:", error);
    }
    dispatch(clearChatbotNewState());
    handleCloseChatbotNew();
    window.location.href = isExamineticsEnabled ? `/#${routes.dashboard}` : `/#${routes.myAssessments}/false`;
  };

  const handleLater = () => {
    // Self-assessment: Redirect to myAssessments/true (can resume later)
    dispatch(clearChatbotNewState());
    handleCloseChatbotNew();
    if (isExamineticsEnabled) {
      history.push(routes.dashboard, { refresh: true });
    } else {
      history.push(`${routes.myAssessments}/true`, { refresh: true });
    }
  };

  const handleYesStart = () => {
    // Caution questions have already been addressed sequentially in the main chatbot flow
    // Open the MovementModal directly
    dispatch(setBiometricModalOpen(true));
  };

  const handleBiometricModalClose = () => {
    dispatch(setBiometricModalOpen(false));
    dispatch(clearChatbotNewState());
    handleCloseChatbotNew();
    window.location.href = isExamineticsEnabled ? `/#${routes.dashboard}` : `/#${routes.myAssessments}/false`;
  };

  const getButtons = () => {
    const invitationType = (invitationTypeName || "").toLowerCase();

    if (invitationType === CONSTANT.REDIRECT_TYPE.INCLINIC) {
      return [{ label: "OK", onClick: handleOK }];
    } else if (invitationType === CONSTANT.REDIRECT_TYPE.TELEMEDICINE) {
      return [
        { label: "Join Now", onClick: () => handleJoinTelemedicineNow(null) },
        { label: "Join Later", onClick: () => handleJoinTelemedicineLater(null) },
      ];
    } else {
      // Default/SELF initial buttons
      return [
        { label: "Yes, start", onClick: handleYesStart },
        { label: "Later", onClick: handleLater },
      ];
    }
  };

  const buttons = getButtons();

  const renderContent = (showButtons = true) => {
    const invitationType = (invitationTypeName || "").toLowerCase();
    const isSelfAssessment = invitationType !== CONSTANT.REDIRECT_TYPE.INCLINIC && invitationType !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;

    if (isExamineticsEnabled && isSelfAssessment && showButtons) {
      return (
        <div className="mmh-chat__text mmh-font-xl current-question">
          <div className="current-question-container">
            <div className="question">
              <div
                className="displayFlex justify-center mmh-align-items-center"
                style={{ padding: "2rem" }}
              >
                <i className="fa fa-spinner fa-spin fa-2x" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mmh-chat__text mmh-font-xl current-question">
        <div className="current-question-container">
          <div className="question">
            <div className="displayFlex justify-start mmh-align-items-center">
              <p style={{ marginBottom: "1rem" }}>{getMessage()}</p>
            </div>
            {showButtons && (
              <div className="responses mt-4">
                {buttons.map((button, index) => (
                  <button
                    key={index}
                    onClick={button.onClick}
                    className="btn btn-outline-brand btn-elevate btn-pill-5 btn-lg btn-ans mr-5 mt-2"
                  >
                    {button.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };





  // If biometric modal is open, render it instead of the prompt
  if (biometricModalOpen) {
    return (
      <>
        {renderContent(false)}
        <MovementModal
          open={biometricModalOpen}
          selectedRegion={{ bodyRegionList: bodyRegionList }}
          testId={testId}
          onClose={handleBiometricModalClose}
          clearAndCloseChatbot={handleCloseChatbotNew}
        />
      </>
    );
  }

  return renderContent(true);
};

export default BiometricPrompt;
